import type { AgentView, DeliveryReason, DeliveryView } from "@walkie/protocol"
import { dur, FAIL_AFTER_MS, ms } from "./time"

export type DState = "accepted" | "delivered" | "presented" | "answered" | "failed"

/**
 * Reasons that make a message the recipient's business. Only these (or a wake
 * delivery) count as failed when they never reach the connector; room and owner
 * FYIs simply wait for the agent's next turn.
 */
const MUST_DELIVER: ReadonlySet<DeliveryReason> = new Set([
	"direct",
	"mention",
	"thread",
	"dependency",
])

export const canFail = (d: DeliveryView) => d.wake || MUST_DELIVER.has(d.reason)

export function deliveryState(d: DeliveryView, now: number): DState {
	if (d.answeredAt) return "answered"
	if (d.presentedAt) return "presented"
	if (d.deliveredAt) return "delivered"
	const t = ms(d.acceptedAt)
	if (t !== null && now - t > FAIL_AFTER_MS && canFail(d)) return "failed"
	return "accepted"
}

export const DGLYPH: Record<DState, string> = {
	accepted: "○",
	delivered: "◐",
	presented: "●",
	answered: "✓",
	failed: "✕",
}

export const DLABEL: Record<DState, string> = {
	accepted: "accepted, not yet delivered",
	delivered: "delivered to connector, not yet seen",
	presented: "presented to the model",
	answered: "answered",
	failed: "not delivered after 2 min",
}

/** Presented, but only a hook said so: the chat transcript never showed it. */
export const hookOnly = (d: DeliveryView) =>
	!!d.presentedAt &&
	d.confirmed != null &&
	d.confirmed !== "transcript" &&
	d.confirmed !== "tool-result"

/** Glyph for a recipient; hook-only presentation gets a hollow centre. */
export const glyphFor = (d: DeliveryView, st: DState) =>
	st === "presented" && hookOnly(d) ? "◎" : DGLYPH[st]

/** State label with the FYI / hook-only nuance the bare state cannot carry. */
export function labelFor(d: DeliveryView, st: DState, now: number): string {
	if (st === "delivered" && !d.wake) return "delivered · waiting for next turn"
	if (st === "accepted" && !canFail(d)) {
		const t = ms(d.acceptedAt)
		if (t !== null && now - t > FAIL_AFTER_MS)
			return "queued · waiting for next turn (FYI, not a failure)"
	}
	if (st === "presented" && hookOnly(d)) return "presented (hook only)"
	return DLABEL[st]
}

export const VIA_LABEL: Record<string, string> = {
	"session-start": "session start",
	prompt: "next prompt",
	"post-tool": "between tool calls",
	"stop-block": "end of turn",
	rewake: "idle wake",
	"tool-result": "tool result",
}

export function viaLabel(via: string | null | undefined, a?: AgentView): string {
	if (!via) return "unknown path"
	const base = VIA_LABEL[via] ?? via
	if (via === "rewake" && a)
		return `${base} (${a.provider === "openai" ? "Codex headless resume" : "Claude asyncRewake"})`
	return base
}

export function confirmLabel(confirmed: string | null | undefined): string {
	if (confirmed === "transcript") return "confirmed in chat transcript"
	if (confirmed === "tool-result") return "confirmed by tool result"
	if (confirmed) return "hook only (not seen in transcript)"
	return "unconfirmed"
}

/** "presented 412 ms after send · via idle wake (Claude asyncRewake) · confirmed in chat transcript" */
export function presentLine(d: DeliveryView, a?: AgentView): string | null {
	const lat = latencyMs(d)
	if (lat === null) return null
	const t = lat < 10_000 ? `${Math.max(0, Math.round(lat))} ms` : dur(lat)
	return `presented ${t} after send · via ${viaLabel(d.presentedVia, a)} · ${confirmLabel(d.confirmed)}`
}

export function latencyMs(d: DeliveryView): number | null {
	const a = ms(d.acceptedAt)
	const p = ms(d.presentedAt)
	return a !== null && p !== null ? p - a : null
}
